/** CSV writing shared by every export surface.
 *
 * Extracted because the escaping and the injection guard were being re-derived
 * per exporter, and a re-derivation is where the guard goes missing — a CSV is
 * opened in Excel or Sheets, where a leading `=` is a formula, not text.
 */

export type CsvCell = string | number | boolean | null | undefined;

/** Defuse spreadsheet formula injection.
 *
 * Excel and Sheets evaluate a cell beginning `=`, `+`, `-`, `@`, tab or CR, so a
 * customer name like `=cmd|'/c calc'!A0` becomes executable on open. Prefixing an
 * apostrophe forces it to text; the apostrophe is not displayed.
 */
export const neutralizeCsv = (value: CsvCell): string => {
  const s = value == null ? "" : String(value);
  return /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
};

/** Quote only when needed, doubling any embedded quote (RFC 4180). */
export const escapeCsvCell = (s: string): string =>
  /[",\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;

/** One CSV cell: neutralized, then escaped. Order matters — escaping first would
 * quote the value and hide the leading `=` from the guard. */
export const csvCell = (value: CsvCell): string => escapeCsvCell(neutralizeCsv(value));

/** A full CSV document, CRLF-terminated per RFC 4180. */
export function toCsv(header: readonly CsvCell[], rows: readonly (readonly CsvCell[])[]): string {
  return [
    header.map(csvCell).join(","),
    ...rows.map((row) => row.map(csvCell).join(",")),
  ].join("\r\n");
}

/** Hand a blob to the browser as a download. */
export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
